import ReactAnd from 'react-and';
import { Button } from '@components/Clickables/Buttons/Button';
import { IFAQData } from '@components/FAQ/FAQ';
import { SpeciesTopBaitNodeFields } from '@generated/graphql';

interface IProps {
  habitatFaq?: string;
  handleCTAClick: () => void;
  speciesName: string;
  topBaits?: SpeciesTopBaitNodeFields[];
}

const MAX_TOP_BAITS = 4;

const getTopBaitCategories = (topBaits?: SpeciesTopBaitNodeFields[]): string[] => {
  if (!topBaits) {
    return [];
  }
  const seen = new Set<string>();
  const categories: string[] = [];
  for (const bait of topBaits) {
    const title = bait.productUnit.product.category.title.toLowerCase();
    if (title && !seen.has(title)) {
      seen.add(title);
      categories.push(title);
      if (categories.length === MAX_TOP_BAITS) {
        break;
      }
    }
  }
  return categories;
};

const FISHBRAIN_APP_LABEL = 'Fishbrain app';

const FishbrainAppButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    onClick={onClick}
    size="sm"
    className="body-text-sm text-primary font-semibold"
    variant="textNone"
  >
    {FISHBRAIN_APP_LABEL}
  </Button>
);

export const getFAQData = ({
  habitatFaq,
  handleCTAClick,
  speciesName,
  topBaits,
}: IProps): IFAQData[] => {
  const topBaitCategories = getTopBaitCategories(topBaits);

  const baitParagraph1Suffix =
    ' — but the best choice shifts with water conditions, season, and local pressure.';
  const baitFallbackParagraph1 = `The best bait for ${speciesName} shifts with water conditions, season, and local pressure.`;

  const baitParagraph1String =
    topBaitCategories.length > 0
      ? `${speciesName} go for ${topBaitCategories.join(', ')}${baitParagraph1Suffix}`
      : baitFallbackParagraph1;

  const baitParagraph2Prefix = 'BiteGuide in the ';
  const baitParagraph2Suffix =
    " crunches real catch reports against weather, water conditions, and seasonal patterns to tell you exactly what's working right now. Download the app and let BiteGuide do the guessing for you.";

  const faqData: IFAQData[] = [
    {
      id: 'bait',
      title: `🎣 What bait works best for ${speciesName}?`,
      body: (
        <>
          <p className="mb-3">
            {topBaitCategories.length > 0 ? (
              <>
                {`${speciesName} go for `}
                <ReactAnd>{topBaitCategories}</ReactAnd>
                {baitParagraph1Suffix}
              </>
            ) : (
              baitFallbackParagraph1
            )}
          </p>
          <p className="m-0">
            Want the answer for <em>your</em> spot, today? {baitParagraph2Prefix}
            <FishbrainAppButton onClick={handleCTAClick} />
            {baitParagraph2Suffix}
          </p>
        </>
      ),
      bodyString: `${baitParagraph1String} Want the answer for your spot, today? ${baitParagraph2Prefix}${FISHBRAIN_APP_LABEL}${baitParagraph2Suffix}`,
    },
  ];

  if (habitatFaq) {
    const habitatParagraph2Prefix = 'BiteGuide in the ';
    const habitatParagraph2Suffix =
      ' narrows it down using real catch data and conditions near you, so you know where to cast, not just where to look. Grab the app and fish smarter.';

    faqData.push({
      id: 'habitat',
      title: `📍 Where do ${speciesName} live?`,
      body: (
        <>
          <p className="mb-3">{habitatFaq}</p>
          <p className="m-0">
            {habitatParagraph2Prefix}
            <FishbrainAppButton onClick={handleCTAClick} />
            {habitatParagraph2Suffix}
          </p>
        </>
      ),
      bodyString: `${habitatFaq} ${habitatParagraph2Prefix}${FISHBRAIN_APP_LABEL}${habitatParagraph2Suffix}`,
    });
  }

  return faqData;
};
